import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { ClientsListResponse } from "@/hooks/queries/client-types";

// First TanStack Query data hook in the codebase (Story 5.7 establishes the
// hooks/queries/ convention). Wraps `apiFetch` + `useQuery` — no manual
// fetch/useEffect, no bespoke fetch wrapper. `skipAuthRefresh` is deliberately
// OMITTED: this is a post-session data call and MUST ride the silent-refresh path
// (opposite of the pre-session auth screens).

/** Root key for every clients query — `invalidateQueries(["clients"])` matches all pages. */
export const CLIENTS_QUERY_KEY = "clients";

export interface ListClientsParams {
  /** 1-indexed page number (backend default 1). */
  page?: number;
  /** Page size (backend default 25, max 100). */
  limit?: number;
  /** Include archived (soft-deleted) clients (Story 5.5; backend default false). */
  includeArchived?: boolean;
}

const DEFAULT_LIMIT = 25;

/** Stable, fully-resolved query key so pages/filters cache independently. */
export function clientsListQueryKey(page: number, limit: number, includeArchived: boolean) {
  return [CLIENTS_QUERY_KEY, { page, limit, includeArchived }] as const;
}

export function useListClients(params: ListClientsParams = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? DEFAULT_LIMIT;
  const includeArchived = params.includeArchived ?? false;

  return useQuery({
    queryKey: clientsListQueryKey(page, limit, includeArchived),
    queryFn: () =>
      apiFetch<ClientsListResponse>(
        `/clients?page=${page}&limit=${limit}&include_archived=${includeArchived}`,
      ),
    // Keep the current page visible while the next page loads, so paging through a
    // long client list doesn't flash the full-screen loading state each step.
    placeholderData: keepPreviousData,
    // gh-471 — the clients list is ONLINE-ONLY by spec (no IDB cache; caching it is the deferred
    // Option B). But under the default `networkMode: "online"` React Query PAUSES the query offline
    // and it hangs on "Loading clients…" forever. `"always"` runs `queryFn` so a genuine offline
    // boot settles to `isError` (a transport `ApiError` status 0) FAST — the screens then render a
    // calm offline notice (`isOfflineError`) instead of an infinite spinner. `retry: false` avoids
    // 3× retry churn before that settle. NO IDB fallback here — this only fixes the hung spinner.
    networkMode: "always",
    retry: false,
  });
}
